import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const areaCodes = [{
  code: '253',
  src: 'https://cdn.magicpatterns.com/uploads/68rE1RFxKTaMLnZX9bTQ6m/253.png'
}, {
  code: '206',
  src: 'https://cdn.magicpatterns.com/uploads/6RRJ8Cb2MtYWCN2KCSKtBN/206.png'
}, {
  code: '425',
  src: 'https://cdn.magicpatterns.com/uploads/g4GXqv7wQ4H6HHSd1TBZ5s/425.png'
}];
const GRAVEL_SRC = 'https://cdn.magicpatterns.com/uploads/tqXEcbNCcYC2dBxg9zT8wG/GRAVEL.png';
// Cadence: 253 is the primary number and holds for HOLD_SECONDS (1 min).
// The alternates (206, 425) glitch in and out — they appear briefly in
// quick succession before snapping back to 253. Total off-253 time
// is ~1 second of glitch flicker.
const HOLD_SECONDS = 60;
const GLITCH_SECONDS = 0.2; // duration of each glitch in / out
const ALT_HOLD_SECONDS = 0.15; // how long 206 / 425 sit on screen between glitches
const FIRST_HOLD_SECONDS = 1.5;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  invertOnLight?: boolean;
  /**
   * Multiplier on the hold time between rotations. 1 = base 60s hold (default).
   * 3 = 180s hold (used by footer for a slower cadence).
   */
  cycleEvery?: number;
}
/**
 * Logo lockup with a static "Gravel" wordmark layered on top of a rotating
 * area code. 253 holds steady; 206 and 425 glitch in and out like a
 * flickering signal before the lockup snaps back to 253. Mounts on a
 * randomized area code so the page doesn't always land on 253.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  cycleEvery = 1
}: LogoProps) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * areaCodes.length));
  const [isFirstCycle, setIsFirstCycle] = useState(true);
  const current = areaCodes[index];
  useEffect(() => {
    let holdMs: number;
    if (isFirstCycle) {
      holdMs = FIRST_HOLD_SECONDS * 1000;
    } else if (current.code === '253') {
      // Primary number — hold here for the full minute.
      holdMs = HOLD_SECONDS * 1000 * cycleEvery;
    } else {
      // 206 / 425 — flash briefly, then move on.
      holdMs = ALT_HOLD_SECONDS * 1000;
    }
    const timeout = setTimeout(() => {
      setIndex(i => (i + 1) % areaCodes.length);
      if (isFirstCycle) setIsFirstCycle(false);
    }, holdMs);
    return () => clearTimeout(timeout);
  }, [index, isFirstCycle, cycleEvery, current.code]);
  const invertClass = invertOnLight ? 'dark:brightness-100 brightness-0' : '';
  // 253 enters cleanly with a smooth fade. 206 / 425 enter and exit with
  // a flickery glitch — opacity stutter + horizontal jitter + brief skew.
  const isAlt = current.code !== '253';
  return <span className={`relative inline-block overflow-hidden ${className}`} aria-label={`${current.code}Gravel`} data-id="element-810">
      {/* Sizer: GRAVEL defines the layout dimensions and stays static on top */}
      <img src={GRAVEL_SRC} alt="" aria-hidden="true" className={`${imgClassName} ${invertClass} block relative z-10 pointer-events-none`} data-id="element-811" />

      {/* Rotating area code with glitch in/out for the alternates. */}
      <AnimatePresence initial={false} data-id="element-812">
        <motion.img key={current.code} src={current.src} alt={current.code} className={`${imgClassName} ${invertClass} absolute inset-0 z-0`} initial={isAlt ? {
        opacity: 0,
        x: 0,
        skewX: '0deg'
      } : {
        opacity: 0
      }} animate={isAlt ? {
        opacity: [0, 1, 0.2, 1, 0.5, 1],
        x: [-6, 5, -4, 3, 0],
        skewX: ['0deg', '-4deg', '5deg', '-2deg', '0deg']
      } : {
        opacity: 1
      }} exit={isAlt ? {
        opacity: [1, 0.4, 0.9, 0.2, 0],
        x: [0, -5, 6, -3, 0],
        skewX: ['0deg', '4deg', '-3deg', '2deg', '0deg']
      } : {
        opacity: 0
      }} transition={{
        duration: isAlt ? GLITCH_SECONDS : 0.25,
        ease: isAlt ? 'linear' : 'easeOut'
      }} data-id="element-813" />
      </AnimatePresence>
    </span>;
}